import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { environment } from '@environments/environment';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { BaseApiParams } from '@services/Types';
import { getCountryCodes } from '@utils/CountryUtils';
import { getCollectionsLiveDataParams, getIsUsingCollectionLiveData } from '@utils/collectionUtils/CollectionUtils';
import axios from 'axios';
import { CountryAvailability, ProductGroupAvailability } from './Models';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';

const constructAvailabilityParams = (
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
  requireAllPgLoadedInCollections: boolean = true, //default value
) => {
  const collectionLiveParams = getAvailabilityCollectionParams(
    baseApiParams,
    collectionDataType,
    requireAllPgLoadedInCollections,
  );
  const productGroup = getProductGroupParams(baseApiParams);

  /**
   * Reformat a custom weeks range string example from "W1 2024 - W10 2024" to "Week 1 2024 - Week 10 2024".
   *
   * - If the period is custom weeks and already in the expected format ("Week X YYYY - Week Y YYYY"), it is returned as-is.
   *
   * @param period - The period range string to be reformatted.
   * @returns A properly formatted custom weeks range string.
   */
  const reformatCustomWeeksPeriod = (period: string): string => {
    const expectedFormat = /^Week \d{1,2} \d{4} - Week \d{1,2} \d{4}$/;
    if (expectedFormat.test(period)) {
      return period;
    }
    return period.replace(/W(\d+)/g, 'Week $1');
  };

  return {
    clientId: baseApiParams.clientId,
    productType: baseApiParams.productType,
    productDataType: ProductDataType.PerformanceAnalyzer,
    country: getCountryCodes(baseApiParams.countries),
    periodType: baseApiParams.periodType.toUpperCase().replace(/\s/g, ''),
    period:
      baseApiParams.periodType === Periodicity.CustomWeeks
        ? reformatCustomWeeksPeriod(baseApiParams.period)
        : baseApiParams.period,
    ...productGroup,
    ...collectionLiveParams,
  };
};

const getAvailabilityCollectionParams = (
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
  requireAllPgLoadedInCollections: boolean = true, //default value
) => {
  const isUsingCollectionLiveData = getIsUsingCollectionLiveData(baseApiParams.period, collectionDataType);

  return getCollectionsLiveDataParams(
    isUsingCollectionLiveData,
    baseApiParams.periodType,
    baseApiParams.comparisonPeriod,
    requireAllPgLoadedInCollections,
  );
};

const getProductGroupParams = (baseApiParams: BaseApiParams) => {
  return baseApiParams.productGroup.length > 0
    ? { productGroup: baseApiParams.productGroup.map((pg) => pg.value) }
    : {};
};

export const getCountryAvailability = async (
  controller: AbortController,
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
): Promise<CountryAvailability> => {
  try {
    const params = constructAvailabilityParams(baseApiParams, collectionDataType);

    const { data } = await axios.post<CountryAvailability>(
      `${environment.baseUrl}/v2/collection/country-availability`,
      params,
      {
        signal: controller.signal,
      },
    );
    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err.message}`);
  }
};

export const getProductGroupAvailability = async (
  controller: AbortController,
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
  requireAllPgLoadedInCollections: boolean = true, //default value
): Promise<ProductGroupAvailability> => {
  try {
    const params = constructAvailabilityParams(baseApiParams, collectionDataType, requireAllPgLoadedInCollections);
    const url = `${environment.baseUrl}/v2/collection/product-group-availability`;
    const { data } = await axios.post<ProductGroupAvailability>(url, params, {
      signal: controller.signal,
    });
    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err.message}`);
  }
};
